import { CdkDragMove } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';

const TranslateHandleOffset = -10;
type CornerId = 'corner1' | 'corner2' | 'corner3' | 'corner4';

/**
 *
 *           T
 *    C1-----------C2
 *    |             |
 *    |             |
 *    |             |
 *    C4-----------C3
 *
 *    In `dragMoved`, Angular tells us that a corner has been dragged.
 *    We use the dragged handle's new position to set the positions of the adjacent corner.
 *    E.g., corner1's movement effects the y position of C2
 *    and the x position of C4.
 *
 *    `setHandle` updates the handle's state, and updates the dom.
 *
 *     Later, `updateTranslation` uses the new corner locations
 *     to reposition the translation handle.
 *
 *     Finally, `updateTextarea` updates the native element's
 *     transform, and the changes are pushed to the dom.
 */
@Component({
  selector: 'app-view-annotate-label',
  templateUrl: './view-annotate-label.component.html',
  styleUrls: ['./view-annotate-label.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewAnnotateLabelComponent implements OnInit, OnDestroy {
  @ViewChild('textRef', { static: true }) textRef: ElementRef;

  @Input()
  public get id(): string {
    return this._id;
  }
  public set id(value: string) {
    this._id = value;
    this.initialized = false;
  }
  private _id: string;

  @Input()
  initialPos: { x: number; y: number };

  @Input()
  public set color(value: string) {
    this.renderer.setStyle(this.textRef.nativeElement, 'color', value);
  }

  @Input()
  public set exporting(isExporting: boolean) {
    if (isExporting) {
      this.active = false;
      this.labelActive.next(undefined);
    }
  }

  @Input()
  public set size(value: string) {
    this.renderer.setStyle(
      this.textRef.nativeElement,
      'font-size',
      `${value}px`
    );
  }

  active = true;
  initialized = false;

  @Output()
  labelActive = new EventEmitter();

  @Output()
  labelEmpty = new EventEmitter();

  dragHandles = {
    translate: { x: 0, y: 0 },
    corner1: { x: 0, y: 0 },
    corner2: { x: 0, y: 0 },
    corner3: { x: 0, y: 0 },
    corner4: { x: 0, y: 0 },
  };

  handleCoords = {
    translate: { x: 0, y: 0 },
    corner1: { x: 0, y: 0 },
    corner2: { x: 0, y: 0 },
    corner3: { x: 0, y: 0 },
    corner4: { x: 0, y: 0 },
  };

  textareaTransform = {
    x: 0,
    y: 0,
    width: 110,
    height: 40,
  };

  mouseDownListener: () => void;
  backspaceListener: () => void;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.initHandles();
    this.initInteractionListeners();
    setTimeout(() => {
      this.textRef.nativeElement.focus();
      this.initialized = true;
    });
  }

  ngOnDestroy() {
    this.textRef.nativeElement.removeEventListener(
      'keydown',
      this.handleTextKeyDown
    );

    this.textRef.nativeElement.removeEventListener(
      'mousedown',
      this.handleTextboxMouseDown
    );

    this.mouseDownListener();
    this.backspaceListener();
  }

  private initHandles() {
    this.textareaTransform.x = this.initialPos.x;
    this.textareaTransform.y = this.initialPos.y;
    const { x, y, width, height } = this.textareaTransform;

    this.dragHandles.corner1 = { x, y };
    this.dragHandles.corner2 = { x: x + width, y };
    this.dragHandles.corner3 = { x: x + width, y: y + height };
    this.dragHandles.corner4 = { x, y: y + height };
    this.dragHandles.translate = {
      x: x + width / 2,
      y: y + TranslateHandleOffset,
    };

    this.handleCoords.corner1 = { ...this.dragHandles.corner1 };
    this.handleCoords.corner2 = { ...this.dragHandles.corner2 };
    this.handleCoords.corner3 = { ...this.dragHandles.corner3 };
    this.handleCoords.corner4 = { ...this.dragHandles.corner4 };
    this.handleCoords.translate = { ...this.dragHandles.translate };
    this.updateTextarea();
  }

  private initInteractionListeners() {
    this.mouseDownListener = this.renderer.listen(
      'document',
      'mousedown',
      this.handleDocumentMouseDown
    );

    this.backspaceListener = this.renderer.listen(
      'document',
      'keydown',
      this.handleDocumentBackspace
    );

    this.textRef.nativeElement.addEventListener(
      'mousedown',
      this.handleTextboxMouseDown
    );

    this.textRef.nativeElement.addEventListener(
      'keydown',
      this.handleTextKeyDown
    );
  }

  private handleDocumentBackspace = (e: KeyboardEvent) => {
    const { activeElement } = this.textRef.nativeElement;
    if (e.key === 'Backspace' && this.active && !activeElement) {
      this.deleteLabel();
    }
  }

  private handleTextboxMouseDown = (e: MouseEvent) => {
    this.active = true;
    this.labelActive.next(this.id);
    this.refreshHandles();
  }

  private refreshHandles() {
    this.dragHandles.corner1 = { ...this.handleCoords.corner1 };
    this.dragHandles.corner2 = { ...this.handleCoords.corner2 };
    this.dragHandles.corner3 = { ...this.handleCoords.corner3 };
    this.dragHandles.corner4 = { ...this.handleCoords.corner4 };
    this.dragHandles.translate = { ...this.handleCoords.translate };
    this.changeDetectorRef.markForCheck();
  }

  private handleDocumentMouseDown = (e) => {
    const canvasClick = e.target.className.includes('view-annotate__canvas');
    const labelClick = e.target.className.includes('whiteboard-label__label');
    const selfClick = e.target.dataset.id === this.id;
    const empty = this.isLabelEmpty();

    if (empty && !selfClick && this.initialized) {
      this.deleteLabel();
    } else if (canvasClick || (labelClick && !selfClick)) {
      this.active = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  private isLabelEmpty() {
    // account for Safari inserting \n even when empty
    return (
      this.textRef.nativeElement.innerText === '' ||
      this.textRef.nativeElement.innerText === '\n'
    );
  }

  private deleteLabel() {
    this.labelEmpty.next(this.id);
  }

  private handleTextKeyDown = (e: KeyboardEvent) => {
    // stop propagation, so the camera doesn't move on WASD, etc.
    e.stopPropagation();

    if (this.isLabelEmpty() && e.key === 'Backspace') {
      this.deleteLabel();
      return;
    }

    const height = this.textRef.nativeElement.scrollHeight;
    const { corner1, corner3, corner4 } = this.handleCoords;
    const newBottomY = corner1.y + height;

    this.handleCoords.corner3 = { ...corner3, y: newBottomY };
    this.handleCoords.corner4 = { ...corner4, y: newBottomY };
    this.dragHandles.corner3 = { ...this.handleCoords.corner3 };
    this.dragHandles.corner4 = { ...this.handleCoords.corner4 };

    this.updateTextarea();
    this.changeDetectorRef.markForCheck();
  }

  private updateTextarea() {
    // update textarea transform
    const { corner1, corner2, corner3 } = this.handleCoords;
    this.textareaTransform.x = corner1.x;
    this.textareaTransform.y = corner1.y;

    this.textareaTransform.width = corner2.x - corner1.x;
    this.textareaTransform.height = corner3.y - corner2.y;

    // write to dom
    this.renderer.setStyle(
      this.textRef.nativeElement,
      'transform',
      `translate(${this.textareaTransform.x}px, ${this.textareaTransform.y}px)`
    );

    this.renderer.setStyle(
      this.textRef.nativeElement,
      'width',
      `${this.textareaTransform.width}px`
    );

    this.renderer.setStyle(
      this.textRef.nativeElement,
      'height',
      `${this.textareaTransform.height}px`
    );
  }

  private updateTranslateHandle() {
    const { corner1, corner2 } = this.handleCoords;
    const handleCenter = (corner2.x - corner1.x) / 2 + corner1.x;
    const handleY = corner1.y + TranslateHandleOffset;

    this.handleCoords.translate = {
      x: handleCenter,
      y: handleY,
    };

    this.dragHandles.translate = { ...this.handleCoords.translate };
  }

  private setHandle(
    corner: CornerId,
    axis: 'x' | 'y',
    handlePos: { x: number; y: number }
  ) {
    this.handleCoords[corner] = {
      ...this.handleCoords[corner],
      [axis]: handlePos[axis],
    };
    this.dragHandles[corner] = { ...this.handleCoords[corner] };
  }

  private addDiffToHandle(corner: CornerId, diff: { x: number; y: number }) {
    this.handleCoords[corner] = {
      x: this.handleCoords[corner].x + diff.x,
      y: this.handleCoords[corner].y + diff.y,
    };

    this.dragHandles[corner] = { ...this.handleCoords[corner] };
  }

  translateHandleMoved($event: CdkDragMove) {
    const handlePos = $event.source._dragRef.getFreeDragPosition();

    const diff = {
      x: handlePos.x - this.handleCoords.translate.x,
      y: handlePos.y - this.handleCoords.translate.y,
    };

    this.handleCoords.translate = { ...handlePos };
    this.addDiffToHandle('corner1', diff);
    this.addDiffToHandle('corner2', diff);
    this.addDiffToHandle('corner3', diff);
    this.addDiffToHandle('corner4', diff);

    this.updateTextarea();
  }

  dragMoved($event: CdkDragMove, id: CornerId) {
    const handlePos = $event.source._dragRef.getFreeDragPosition();
    this.handleCoords[id] = { ...handlePos };

    if (id === 'corner1') {
      this.setHandle('corner2', 'y', handlePos);
      this.setHandle('corner4', 'x', handlePos);
    }

    if (id === 'corner2') {
      this.setHandle('corner1', 'y', handlePos);
      this.setHandle('corner3', 'x', handlePos);
    }

    if (id === 'corner3') {
      this.setHandle('corner2', 'x', handlePos);
      this.setHandle('corner4', 'y', handlePos);
    }

    if (id === 'corner4') {
      this.setHandle('corner1', 'x', handlePos);
      this.setHandle('corner3', 'y', handlePos);
    }

    this.updateTranslateHandle();
    this.updateTextarea();
  }
}
